/**
 * Scheduled task form component for creating and editing scheduled browser automation tasks
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Clock, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'

import type { CreateScheduledTaskRequest } from '../../types/anchor-browser'

// Form validation schema
const scheduledTaskFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  task: z.string().min(10, 'Task description must be at least 10 characters'),
  cron_expression: z.string().min(1, 'Schedule is required'),
  timezone: z.string().optional(),
  enabled: z.boolean().optional(),
  llm_model: z.enum(['gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'gpt-4.1-mini', 'gemini-2.5-flash', 'claude-sonnet-4-20250514']).optional(),
  use_adblock: z.boolean().optional(),
  use_proxy: z.boolean().optional(),
  proxy_country_code: z.enum(['us', 'fr', 'it', 'jp', 'au', 'de', 'fi', 'ca']).optional(),
  highlight_elements: z.boolean().optional(),
  save_browser_data: z.boolean().optional(),
  enable_public_share: z.boolean().optional(),
  browser_viewport_width: z.number().min(320).max(3840).optional(),
  browser_viewport_height: z.number().min(240).max(2160).optional(),
  max_agent_steps: z.number().min(1).max(200).optional(),
})

type ScheduledTaskFormData = z.infer<typeof scheduledTaskFormSchema>

interface ScheduledTaskFormProps {
  onSubmit: (data: CreateScheduledTaskRequest) => void
  isLoading?: boolean
  defaultValues?: Partial<ScheduledTaskFormData>
  mode?: 'create' | 'edit'
}

const LLM_MODELS: { value: string; label: string }[] = [
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'gpt-4.1', label: 'GPT-4.1' },
  { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
]

const PROXY_COUNTRIES: { value: string; label: string }[] = [
  { value: 'us', label: 'United States' },
  { value: 'fr', label: 'France' },
  { value: 'it', label: 'Italy' },
  { value: 'jp', label: 'Japan' },
  { value: 'au', label: 'Australia' },
  { value: 'de', label: 'Germany' },
  { value: 'fi', label: 'Finland' },
  { value: 'ca', label: 'Canada' },
]

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
]

export function ScheduledTaskForm({ 
  onSubmit, 
  isLoading = false, 
  defaultValues,
  mode = 'create'
}: ScheduledTaskFormProps) {
  const form = useForm<ScheduledTaskFormData>({
    resolver: zodResolver(scheduledTaskFormSchema),
    defaultValues: {
      enabled: true,
      llm_model: 'gemini-2.5-flash',
      use_adblock: true,
      use_proxy: true,
      proxy_country_code: 'us',
      highlight_elements: true,
      save_browser_data: false,
      enable_public_share: false,
      browser_viewport_width: 1280,
      browser_viewport_height: 960,
      max_agent_steps: 75,
      timezone: 'UTC',
      ...defaultValues,
    },
  })

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form
  const watchedValues = watch()

  const handleFormSubmit = (data: ScheduledTaskFormData) => {
    const cleanedData: CreateScheduledTaskRequest = {
      ...data,
      timezone: data.timezone || 'UTC',
    }

    onSubmit(cleanedData)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Scheduled Task Information
          </CardTitle>
          <CardDescription>
            Configure the basic details for your scheduled browser automation task
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="name">Task Name *</Label>
            <Input
              id="name"
              placeholder="Enter a descriptive name for this scheduled task"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="task">Task Description *</Label>
            <Textarea
              id="task"
              placeholder="Describe what you want the browser automation to do..."
              className="min-h-[100px]"
              {...register('task')}
            />
            {errors.task && (
              <p className="text-sm text-destructive mt-1">{errors.task.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="enabled">Enabled</Label>
              <p className="text-sm text-muted-foreground">
                Whether this scheduled task should run automatically
              </p>
            </div>
            <Switch
              id="enabled"
              checked={watchedValues.enabled}
              onCheckedChange={(checked) => setValue('enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Schedule Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Schedule Configuration
          </CardTitle>
          <CardDescription>
            Define when and how often this task should run
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="timezone">Timezone</Label>
            <Select
              value={watchedValues.timezone}
              onValueChange={(value) => setValue('timezone', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="cron_expression">Schedule (Cron Expression)</Label>
            <Input
              id="cron_expression"
              placeholder="0 0 * * * (daily at midnight)"
              {...register('cron_expression')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use cron format: minute hour day month weekday
            </p>
            {errors.cron_expression && (
              <p className="text-sm text-destructive mt-1">{errors.cron_expression.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Task Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Task Configuration</CardTitle>
          <CardDescription>
            Configure how the browser automation should behave
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="llm_model">AI Model</Label>
            <Select
              value={watchedValues.llm_model}
              onValueChange={(value) => setValue('llm_model', value as any)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select AI model" />
              </SelectTrigger>
              <SelectContent>
                {LLM_MODELS.map((model) => (
                  <SelectItem key={model.value} value={model.value}>
                    {model.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="viewport_width">Viewport Width</Label>
              <Input
                id="viewport_width"
                type="number"
                min="320"
                max="3840"
                {...register('browser_viewport_width', { valueAsNumber: true })}
              />
            </div>
            <div>
              <Label htmlFor="viewport_height">Viewport Height</Label>
              <Input
                id="viewport_height"
                type="number"
                min="240"
                max="2160"
                {...register('browser_viewport_height', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="max_steps">Maximum Steps</Label>
            <Input
              id="max_steps"
              type="number"
              min="1"
              max="200"
              {...register('max_agent_steps', { valueAsNumber: true })}
            />
          </div>

          {/* Feature Toggles */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="use_adblock">Ad Blocker</Label>
                <p className="text-sm text-muted-foreground">Block advertisements and trackers</p>
              </div>
              <Switch
                id="use_adblock"
                checked={watchedValues.use_adblock}
                onCheckedChange={(checked) => setValue('use_adblock', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="use_proxy">Use Proxy</Label>
                <p className="text-sm text-muted-foreground">Required for captcha solving</p>
              </div>
              <Switch
                id="use_proxy"
                checked={watchedValues.use_proxy}
                onCheckedChange={(checked) => setValue('use_proxy', checked)}
              />
            </div>

            {watchedValues.use_proxy && (
              <div>
                <Label>Proxy Country</Label>
                <Select
                  value={watchedValues.proxy_country_code}
                  onValueChange={(value) => setValue('proxy_country_code', value as any)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROXY_COUNTRIES.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="highlight_elements">Highlight Elements</Label>
                <p className="text-sm text-muted-foreground">Visually highlight interactive elements</p>
              </div>
              <Switch
                id="highlight_elements"
                checked={watchedValues.highlight_elements}
                onCheckedChange={(checked) => setValue('highlight_elements', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="save_browser_data">Save Browser Data</Label>
                <p className="text-sm text-muted-foreground">Persist cookies and session data</p>
              </div>
              <Switch
                id="save_browser_data"
                checked={watchedValues.save_browser_data}
                onCheckedChange={(checked) => setValue('save_browser_data', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enable_public_share">Public Sharing</Label>
                <p className="text-sm text-muted-foreground">Allow others to view task results</p>
              </div>
              <Switch
                id="enable_public_share"
                checked={watchedValues.enable_public_share}
                onCheckedChange={(checked) => setValue('enable_public_share', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Alert */}
      <Alert>
        <AlertDescription>
          Scheduled tasks will run automatically according to the configured schedule. 
          You can enable/disable them at any time and view execution history.
        </AlertDescription>
      </Alert>

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isLoading}>
          {isLoading 
            ? (mode === 'create' ? 'Creating...' : 'Updating...') 
            : (mode === 'create' ? 'Create Scheduled Task' : 'Update Scheduled Task')
          }
        </Button>
      </div>
    </form>
  )
}
